import { Component, Event, EventEmitter, Fragment, FunctionalComponent, getAssetPath, h, Prop } from '@stencil/core'
import { GameStore } from 'src/gamification/ui'
import { dismissBanner } from 'src/gamification/ui'
import { dayjs } from 'src/lib/dayjs'
import { IUser } from 'src/page/admin/types'
import { classes } from 'src/utils/css'

import { IBanner } from '../../app'
import { InertiaForm } from '../../inertia/form/form'
import { InlineIconText } from '../../ui/components'
import { IBannerPromptCall } from '../multi-step-modal/multi-step-modal'

const PreferenceBullet: FunctionalComponent<{ disabled?: boolean }> = ({ disabled = false }, children) => {
  return (
    <div class='flex gap-6px items-start'>
      <ui-icon
        icon={disabled ? 'x-fat' : 'check-fat'}
        class={`pt-3px text-base ${disabled ? 'text-surface-highest' : 'text-green'}`}
      />
      <span class='text-base font-medium text-gray-10'>{children}</span>
    </div>
  )
}

@Component({
  tag: 'onboarding-education-modal',
})
export class OnboardingEducationModal {
  @Event() onboardingEducationModalClosed: EventEmitter
  @Event() tutorial: EventEmitter
  @Prop() banner: IBanner

  @Prop() currentUser: IUser
  @Prop() nextExternalCall: IBannerPromptCall
  @Prop() customizePageUrl: string
  @Prop() startTestCallUrl: string
  @Prop() connectZoomUrl: string
  @Prop() scheduleCallUrl: string
  @Prop() completedTutorial = false
  @Prop() tutorialVersion = 1

  render() {
    const hidePoints = !GameStore.state.isEnabled

    return (
      <ui-dialog
        onClosed={(e) => {
          e.stopPropagation()
          this.onboardingEducationModalClosed.emit()
        }}
        content={({ dialog }) => (
          <div class='flex relative flex-col p-3 mx-auto bg-surface-upper rounded-md border-1 border-surface-high'>
            <ui-icon
              onClick={async () => {
                await dialog.close()
              }}
              class='ml-auto text-3xl text-glyph-light hover:text-glyph-base cursor-pointer'
              icon='x'
              tabIndex={0}
            />

            <div class='flex flex-col items-center spacing-y-2'>
              {this.completedTutorial ? (
                <Fragment>
                  <span class='font-medium text-glyph-lighter uppercase'>
                    Tutorial Completed!
                    <span hidden={hidePoints} class='font-extrabold text-info-warn'>
                      {' '}
                      +5 Points
                    </span>
                  </span>
                  <span class='max-w-md text-3xl font-bold text-center'>
                    Now try Fathom on a test Zoom call
                    {!hidePoints && (
                      <Fragment>
                        <br />
                        to earn another{' '}
                        <InlineIconText icon='coin-star-fill' class='text-info-warn whitespace-nowrap'>
                          <span class='font-extrabold'>5 Points</span>
                        </InlineIconText>
                      </Fragment>
                    )}
                  </span>
                </Fragment>
              ) : (
                <Fragment>
                  <span class='font-medium text-glyph-lighter uppercase'>One last thing</span>
                  <span class='text-3xl font-bold'>
                    Learn how to use Fathom
                    {!hidePoints && (
                      <Fragment>
                        {' '}
                        and earn{' '}
                        <InlineIconText icon='coin-star-fill' class='text-info-warn whitespace-nowrap'>
                          <span class='font-extrabold'>5 Points</span>
                        </InlineIconText>
                      </Fragment>
                    )}
                  </span>
                </Fragment>
              )}

              <div class='flex flex-col lg:flex-row justify-items-stretch content-center items-center lg:items-start p-10 spacing-y-4 lg:spacing-y-0 lg:spacing-x-10'>
                <div class='flex flex-col items-center mr-10 max-w-xs spacing-y-4'>
                  <div class='relative'>
                    <img
                      src={getAssetPath('./assets/tutorial_preview.png')}
                      class={`h-40 rounded-xl ${classes(this.completedTutorial, 'opacity-50')}`}
                    />
                    <ui-icon
                      hidden={!this.completedTutorial}
                      icon='check-circle'
                      class='absolute top-1/2 left-1/2 text-info-warn bg-surface-upper rounded-full transform -translate-x-1/2 -translate-y-1/2'
                      size='6xl'
                    />
                  </div>
                  <ui-button
                    color='info-warn'
                    icon='slides'
                    iconStyle={{ fontSize: '25px' }}
                    uppercase={false}
                    class='w-64 text-lg'
                    onClick={() => {
                      this.tutorial.emit()
                    }}
                  >
                    <span class='font-extrabold'>Self-Guided Tutorial</span>
                    <span class='ml-1 font-medium'>- 1 min</span>
                  </ui-button>
                  <span class='text-md font-medium text-glyph-lighter'>Tutorial contains 10 slides</span>
                </div>
                <InertiaForm method='post' action={this.startTestCallUrl}>
                  <div class='flex flex-col items-center max-w-xs spacing-y-4'>
                    <div class='relative'>
                      <img
                        src={getAssetPath('./assets/zoom_test_call_640.png')}
                        class={`h-40 rounded-xl ${classes(!this.startTestCallUrl, 'opacity-50')}`}
                      />
                      <div class='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'>
                        {/* button defines its own relative so we get to put this in a div */}
                        <ui-button
                          color='info-warn'
                          bgColor='surface-upper'
                          uppercase={false}
                          class='text-lg'
                          hidden={!!this.startTestCallUrl}
                          href={this.connectZoomUrl}
                          target='_blank'
                        >
                          Connect Zoom to Enable
                        </ui-button>
                      </div>
                    </div>
                    <ui-button
                      color={this.startTestCallUrl ? 'info-warn' : 'gray'}
                      icon='play'
                      iconStyle={{ fontSize: '25px' }}
                      uppercase={false}
                      class='w-64 text-lg'
                      type='submit'
                      disabled={!this.startTestCallUrl}
                    >
                      <span class='font-extrabold'>Start Test Call</span>
                      <span class='ml-1 font-normal'>- 2 mins</span>
                    </ui-button>
                    <span class='text-md font-medium text-glyph-lighter'>Don't worry, she's just a recording</span>
                  </div>
                </InertiaForm>
              </div>

              {this.scheduleCallUrl && (
                <p class='text-md'>
                  Prefer a real call?{' '}
                  <a
                    href={this.scheduleCallUrl}
                    class='text-link underline focus:shadow-outline'
                    target='_blank'
                    onClick={async () => {
                      await dialog.close()
                    }}
                  >
                    Schedule a 15-min onboarding call
                  </a>
                </p>
              )}

              <div class='flex justify-between px-10 pb-10 w-100%'>
                <div class='flex flex-col gap-y-2 pr-3 max-w-lg'>
                  {this.currentUser?.preferences?.auto_record_external?.value ? (
                    <PreferenceBullet>
                      Fathom will auto-record starting with your next external meeting
                      {this.nextExternalCall && (
                        <Fragment>
                          {': '}
                          <span class='font-bold'>{this.nextExternalCall.topic}</span> on{' '}
                          <span class='font-bold'>
                            {dayjs(this.nextExternalCall.started_at).format('MMMM D @ h:mma')}
                          </span>
                        </Fragment>
                      )}
                    </PreferenceBullet>
                  ) : (
                    <PreferenceBullet disabled={true}>
                      <span class='font-bold'>Auto-record is disabled.</span> Click "Start Recording" in the Fathom app
                      when you want it to record and summarize a meeting.
                    </PreferenceBullet>
                  )}
                  {this.currentUser?.preferences?.auto_share === 'recording_and_summary' ? (
                    <PreferenceBullet>
                      AI meeting summaries & recordings will be shared with attendees automatically.
                    </PreferenceBullet>
                  ) : this.currentUser?.preferences?.auto_share === 'summary_only' ? (
                    <PreferenceBullet>
                      AI meeting summaries will be shared with attendees automatically.
                    </PreferenceBullet>
                  ) : (
                    <PreferenceBullet disabled={true}>
                      <span class='font-bold'>Sharing is disabled.</span> AI meeting summaries & recordings will only be
                      shared with attendees when you click "Send Recording"
                    </PreferenceBullet>
                  )}
                </div>
                <div>
                  <button
                    onClick={async (event) => {
                      event.preventDefault()
                      await dismissBanner(this.banner)
                      await dialog.close()
                      window.location.assign(this.customizePageUrl)
                    }}
                    class='flex gap-6px items-center font-semibold text-link hover:underline'
                  >
                    <ui-icon icon='gear' />
                    <span>Edit Settings</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      />
    )
  }
}
